'use client';

import { useState, useTransition } from 'react';
import { AlertTriangle, Check, Shield, X, Zap } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@kit/ui/badge';
import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';
import { cn } from '@kit/ui/utils';

import {
  approveToolInConversationAction,
  rejectToolInConversationAction,
} from './tool-approval-actions';

interface ToolApprovalCardProps {
  messageId: string;
  approval: {
    approval_id: string;
    tool_name: string;
    tool_args: Record<string, unknown>;
    risk_level: number;
    action_summary: string;
    status: string;
  };
  timestamp: string;
}

const RISK_CONFIG = {
  1: {
    labelKey: 'chat:toolApproval.risk.low',
    color: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    icon: Shield,
  },
  2: {
    labelKey: 'chat:toolApproval.risk.medium',
    color: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
    icon: AlertTriangle,
  },
  3: {
    labelKey: 'chat:toolApproval.risk.high',
    color: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    icon: AlertTriangle,
  },
} as const;

export function ToolApprovalCard({
  messageId,
  approval,
  timestamp,
}: ToolApprovalCardProps) {
  const { t } = useTranslation();
  const [isPending, startTransition] = useTransition();
  const [localStatus, setLocalStatus] = useState(approval.status);
  const [error, setError] = useState<string | null>(null);
  const showError = (thrown: unknown) =>
    setError(thrown instanceof Error ? thrown.message : String(thrown));

  const risk =
    RISK_CONFIG[approval.risk_level as keyof typeof RISK_CONFIG] ||
    RISK_CONFIG[2];
  const RiskIcon = risk.icon;

  const handleApprove = () => {
    setError(null);
    startTransition(async () => {
      try {
        await approveToolInConversationAction(messageId, approval.approval_id);
        setLocalStatus('approved');
      } catch (thrown) {
        showError(thrown);
      }
    });
  };

  const handleReject = () => {
    setError(null);
    startTransition(async () => {
      try {
        await rejectToolInConversationAction(
          messageId,
          approval.approval_id,
          t('chat:toolApproval.rejectedReason'),
        );
        setLocalStatus('rejected');
      } catch (thrown) {
        showError(thrown);
      }
    });
  };

  const isPendingApproval = localStatus === 'pending';

  return (
    <div className="max-w-[80%] space-y-1">
      <div
        className={cn(
          'rounded-lg border p-3',
          isPendingApproval
            ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950'
            : localStatus === 'approved'
              ? 'border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950'
              : 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950',
        )}
      >
        {/* Header */}
        <div className="flex items-center gap-2">
          <Zap className="h-4 w-4 text-amber-600 dark:text-amber-400" />
          <span className="text-sm font-medium">
            <Trans i18nKey="chat:toolApproval.title" />
          </span>
          <Badge
            variant="outline"
            className={cn('ml-auto h-5 text-xs', risk.color)}
          >
            <RiskIcon className="mr-1 h-3 w-3" />
            {t('chat:toolApproval.riskBadge', { risk: t(risk.labelKey) })}
          </Badge>
        </div>

        {/* Action summary */}
        <p className="mt-2 text-sm whitespace-pre-wrap">
          {approval.action_summary}
        </p>

        {/* Tool info */}
        <p className="text-muted-foreground mt-1 text-xs">
          {t('chat:toolApproval.tool', { name: approval.tool_name })}
        </p>

        {error && (
          <p
            className="mt-2 text-xs text-red-700 dark:text-red-300"
            role="alert"
          >
            {t('chat:toolApproval.failed', { error })}
          </p>
        )}

        {/* Action buttons or status */}
        {isPendingApproval ? (
          <div className="mt-3 flex gap-2">
            <Button
              size="sm"
              variant="default"
              onClick={handleApprove}
              disabled={isPending}
              className="gap-1"
            >
              <Check className="h-3 w-3" />
              <Trans i18nKey="chat:toolApproval.approve" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleReject}
              disabled={isPending}
              className="gap-1"
            >
              <X className="h-3 w-3" />
              <Trans i18nKey="chat:toolApproval.reject" />
            </Button>
          </div>
        ) : (
          <div className="mt-2">
            <Badge
              variant="outline"
              className={cn(
                'text-xs',
                localStatus === 'approved'
                  ? 'border-green-300 text-green-700 dark:border-green-700 dark:text-green-300'
                  : localStatus === 'rejected'
                    ? 'border-red-300 text-red-700 dark:border-red-700 dark:text-red-300'
                    : 'border-gray-300 text-gray-700 dark:border-gray-700 dark:text-gray-300',
              )}
            >
              {localStatus === 'approved' &&
                t('chat:toolApproval.status.approved')}
              {localStatus === 'rejected' &&
                t('chat:toolApproval.status.rejected')}
              {localStatus === 'expired' &&
                t('chat:toolApproval.status.expired')}
            </Badge>
          </div>
        )}
      </div>

      {/* Timestamp */}
      <div className="text-muted-foreground text-xs">{timestamp}</div>
    </div>
  );
}
